import React, { createContext, useContext, useReducer } from "react";

/**
 * Initial state for UI context
 */
const initialState = {
  loading: false,
  error: null,
  notification: null,
  modal: {
    isOpen: false,
    type: null,
    content: null,
  },
  sidebar: {
    isOpen: false,
    content: null,
  },
};

/**
 * Action types for UI reducer
 */
const ActionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SHOW_NOTIFICATION: "SHOW_NOTIFICATION",
  HIDE_NOTIFICATION: "HIDE_NOTIFICATION",
  OPEN_MODAL: "OPEN_MODAL",
  CLOSE_MODAL: "CLOSE_MODAL",
  OPEN_SIDEBAR: "OPEN_SIDEBAR",
  CLOSE_SIDEBAR: "CLOSE_SIDEBAR",
};

/**
 * Reducer function for UI state management
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New state
 */
function uiReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.SHOW_NOTIFICATION:
      return {
        ...state,
        notification: action.payload,
      };

    case ActionTypes.HIDE_NOTIFICATION:
      return {
        ...state,
        notification: null,
      };

    case ActionTypes.OPEN_MODAL:
      return {
        ...state,
        modal: {
          isOpen: true,
          type: action.payload.type,
          content: action.payload.content,
        },
      };

    case ActionTypes.CLOSE_MODAL:
      return {
        ...state,
        modal: {
          isOpen: false,
          type: null,
          content: null,
        },
      };

    case ActionTypes.OPEN_SIDEBAR:
      return {
        ...state,
        sidebar: {
          isOpen: true,
          content: action.payload,
        },
      };

    case ActionTypes.CLOSE_SIDEBAR:
      return {
        ...state,
        sidebar: {
          isOpen: false,
          content: null,
        },
      };

    default:
      return state;
  }
}

/**
 * UI context
 */
const UIContext = createContext();

/**
 * UI context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function UIProvider({ children }) {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  const setLoading = (loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  };

  /**
   * Set error state
   * @param {string|null} error - Error message
   */
  const setError = (error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  /**
   * Show notification
   * @param {Object} notification - Notification object
   */
  const showNotification = (notification) => {
    dispatch({ type: ActionTypes.SHOW_NOTIFICATION, payload: notification });
  };

  /**
   * Hide notification
   */
  const hideNotification = () => {
    dispatch({ type: ActionTypes.HIDE_NOTIFICATION });
  };

  /**
   * Open modal
   * @param {string} type - Modal type
   * @param {React.ReactNode} content - Modal content
   */
  const openModal = (type, content) => {
    dispatch({
      type: ActionTypes.OPEN_MODAL,
      payload: { type, content },
    });
  };

  /**
   * Close modal
   */
  const closeModal = () => {
    dispatch({ type: ActionTypes.CLOSE_MODAL });
  };

  /**
   * Open sidebar
   * @param {React.ReactNode} content - Sidebar content
   */
  const openSidebar = (content) => {
    dispatch({ type: ActionTypes.OPEN_SIDEBAR, payload: content });
  };

  /**
   * Close sidebar
   */
  const closeSidebar = () => {
    dispatch({ type: ActionTypes.CLOSE_SIDEBAR });
  };

  const value = {
    ...state,
    setLoading,
    setError,
    clearError,
    showNotification,
    hideNotification,
    openModal,
    closeModal,
    openSidebar,
    closeSidebar,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

/**
 * Hook to use UI context
 * @returns {Object} UI context value
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useUIContext() {
  const context = useContext(UIContext);

  if (!context) {
    throw new Error("useUIContext must be used within a UIProvider");
  }

  return context;
}